import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import type { Group } from '@/types';
import ActionMenu from '@/components/base/ActionMenu';

const typeLabels: Record<string, string> = {
  clients: 'Clientes',
  leads: 'Leads',
  suppliers: 'Fornecedores',
  employees: 'Funcionários',
  custom: 'Personalizado',
};

const typeIcons: Record<string, string> = {
  clients: 'ri-user-smile-line',
  leads: 'ri-flashlight-line',
  suppliers: 'ri-truck-line',
  employees: 'ri-team-line',
  custom: 'ri-folder-line',
};

export default function GroupsPage() {
  const { profile } = useAuth();

  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [formData, setFormData] = useState({ name: '', color: '#6B7280', type: 'custom' });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Group | null>(null);
  const [saving, setSaving] = useState(false);

  // Contact counts per group
  const [contactCounts, setContactCounts] = useState<Record<string, number>>({});

  const companyId = profile?.company_id;

  const fetchGroups = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    const { data, error: fetchError } = await supabase
      .from('groups')
      .select('*')
      .eq('company_id', companyId)
      .order('created_at', { ascending: true });

    if (fetchError) {
      setError(fetchError.message);
    } else {
      setGroups((data || []) as Group[]);
    }
    setLoading(false);
  }, [companyId]);

  const fetchContactCounts = useCallback(async () => {
    if (!companyId || groups.length === 0) return;
    
    const counts: Record<string, number> = {};
    
    // Use count: 'exact' per group for 100% accurate counting
    for (const group of groups) {
      try {
        const { count, error: countError } = await supabase
          .from('contacts')
          .select('*', { count: 'exact', head: true })
          .eq('company_id', companyId)
          .eq('group_id', group.id);
        
        if (!countError && count !== null) {
          counts[group.id] = count;
        }
      } catch {
        // Skip individual group errors
      }
    }
    
    setContactCounts(counts);
  }, [companyId, groups]);

  useEffect(() => {
    if (companyId) {
      fetchGroups();
    }
  }, [companyId, fetchGroups]);

  // Fetch counts AFTER groups are loaded
  useEffect(() => {
    if (companyId && groups.length > 0) {
      fetchContactCounts();
    }
  }, [companyId, groups, fetchContactCounts]);

  const openNew = () => {
    setEditingGroup(null);
    setFormData({ name: '', color: '#6B7280', type: 'custom' });
    setModalOpen(true);
  };

  const openEdit = (group: Group) => {
    setEditingGroup(group);
    setFormData({ name: group.name, color: group.color, type: group.type });
    setModalOpen(true);
  };

  const openDelete = (group: Group) => {
    setDeleteTarget(group);
    setDeleteModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!companyId) return;
    setSaving(true);

    if (editingGroup) {
      const { error: updError } = await supabase
        .from('groups')
        .update({ name: formData.name, color: formData.color, type: formData.type })
        .eq('id', editingGroup.id);

      if (!updError) {
        setGroups((prev) => prev.map((g) => (g.id === editingGroup.id ? { ...g, ...formData } : g)));
      }
    } else {
      const { data: inserted, error: insError } = await supabase
        .from('groups')
        .insert({
          company_id: companyId,
          name: formData.name,
          color: formData.color,
          type: formData.type,
        })
        .select()
        .maybeSingle();

      if (!insError && inserted) {
        setGroups((prev) => [...prev, inserted as Group]);
      }
    }

    setSaving(false);
    setModalOpen(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);

    const { error: delError } = await supabase
      .from('groups')
      .delete()
      .eq('id', deleteTarget.id);

    if (!delError) {
      setGroups((prev) => prev.filter((g) => g.id !== deleteTarget.id));
    }

    setSaving(false);
    setDeleteTarget(null);
    setDeleteModalOpen(false);
  };

  // ─── Loading / Error ───
  if (loading) {
    return (
      <div className="space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <div className="h-7 w-24 bg-background-200 rounded animate-pulse" />
            <div className="h-4 w-48 bg-background-100 rounded mt-1 animate-pulse" />
          </div>
        </div>
        <div className="bg-white rounded-xl border border-background-200 p-12 flex items-center justify-center">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-foreground-500">Carregando grupos...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-5">
        <h2 className="text-xl font-heading font-bold text-foreground-950">Grupos</h2>
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-2xl text-red-500" />
          </div>
          <p className="text-sm text-foreground-700 font-medium mb-1">Erro ao carregar grupos</p>
          <p className="text-xs text-foreground-500 mb-4">{error}</p>
          <button
            onClick={fetchGroups}
            className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-heading font-bold text-foreground-950">Grupos</h2>
          <p className="text-sm text-foreground-500 mt-1">Organize seus contatos em grupos e segmentos</p>
        </div>
        <button
          onClick={openNew}
          disabled={saving}
          className="px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
        >
          <i className="ri-add-line text-lg" />
          Novo grupo
        </button>
      </div>

      {/* Grid */}
      {groups.length === 0 ? (
        <div className="bg-white rounded-xl border border-background-200 p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-folder-line text-2xl text-foreground-400" />
          </div>
          <p className="text-sm text-foreground-500 mb-1">Nenhum grupo criado ainda</p>
          <p className="text-xs text-foreground-400">Crie seu primeiro grupo para organizar seus contatos</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((group) => (
            <div
              key={group.id}
              className="bg-white rounded-xl border border-background-200 p-5 hover:border-background-300 transition-colors group"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${group.color}18` }}
                  >
                    <i className={`${typeIcons[group.type] || 'ri-folder-line'} text-xl`} style={{ color: group.color }} />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-foreground-900">{group.name}</h3>
                    <p className="text-xs text-foreground-500 mt-0.5">{typeLabels[group.type] || group.type}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity lg:hidden">
                  <ActionMenu
                    actions={[
                      {
                        label: 'Editar',
                        icon: 'ri-edit-line',
                        iconColor: 'text-primary-500',
                        onClick: () => openEdit(group),
                      },
                      {
                        label: 'Excluir',
                        icon: 'ri-delete-bin-line',
                        danger: true,
                        onClick: () => openDelete(group),
                      },
                    ]}
                  />
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity hidden lg:flex">
                  <Link
                    to={`/contacts?group=${group.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                    title="Ver contatos deste grupo"
                  >
                    <i className="ri-user-search-line text-base" />
                  </Link>
                  <button
                    onClick={() => openEdit(group)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-primary-600 hover:bg-primary-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-edit-line text-base" />
                  </button>
                  <button
                    onClick={() => openDelete(group)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-base" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-3 border-t border-background-100">
                <div className="flex-1">
                  <p className="text-2xl font-heading font-bold text-foreground-950">
                    {contactCounts[group.id] || 0}
                  </p>
                  <p className="text-xs text-foreground-500">contatos</p>
                </div>
                <div className="w-px h-8 bg-background-200" />
                <div className="flex-1">
                  <p className="text-2xl font-heading font-bold text-foreground-950">0</p>
                  <p className="text-xs text-foreground-500">Campanhas</p>
                </div>
                <div className="w-px h-8 bg-background-200" />
                <div className="w-24">
                  <div className="h-2 rounded-full bg-background-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: contactCounts[group.id] ? '60%' : '20%',
                        backgroundColor: group.color,
                      }}
                    />
                  </div>
                  <p className="text-xs text-foreground-400 mt-1 text-right">Ativos</p>
                </div>
              </div>

              {/* Quick action link — always visible on mobile, hover on desktop */}
              <div className="mt-3 pt-3 border-t border-background-100 lg:hidden">
                <Link
                  to={`/contacts?group=${group.id}`}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-background-50 hover:bg-background-100 text-foreground-600 text-xs font-medium rounded-lg transition-colors cursor-pointer"
                >
                  <i className="ri-user-search-line" />
                  Ver contatos do grupo
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Group Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setModalOpen(false)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-[400px] mx-4 p-5 animate-fadeIn">
            <h3 className="text-base font-heading font-semibold text-foreground-950 mb-5">
              {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
            </h3>

            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-1.5">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="Nome do grupo"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Tipo</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                  >
                    <option value="clients">Clientes</option>
                    <option value="leads">Leads</option>
                    <option value="suppliers">Fornecedores</option>
                    <option value="employees">Funcionários</option>
                    <option value="custom">Personalizado</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">Cor</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="w-9 h-9 rounded-lg border border-background-200 cursor-pointer"
                    />
                    <input
                      type="text"
                      value={formData.color}
                      onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                      className="flex-1 px-3 py-2 bg-white border border-background-200 rounded-lg text-sm text-foreground-900 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving || !formData.name.trim()}
                  className="px-5 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  {saving ? 'Salvando...' : editingGroup ? 'Salvar' : 'Criar grupo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setDeleteModalOpen(false)} />
          <div className="relative bg-white rounded-xl border border-background-200 w-full max-w-[400px] mx-4 p-6 animate-fadeIn">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-delete-bin-line text-red-500 text-2xl" />
            </div>
            <h3 className="text-lg font-heading font-semibold text-foreground-950 text-center mb-2">Excluir grupo</h3>
            <p className="text-sm text-foreground-500 text-center mb-6">
              Tem certeza que deseja excluir "{deleteTarget?.name}"? Os contatos deste grupo não serão excluídos.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setDeleteModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-foreground-600 hover:bg-background-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                disabled={saving}
                className="px-5 py-2 bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                {saving ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}